import { decrypt, encrypt, generateKey } from "./crypto";
import { activity } from "./activity-reporter";

type BridgeMessage = {
  type: string;
  payload: any;
  txn: string;
  __source: "host" | "remote";
  __origin?: string;
};

type Listener = (message: BridgeMessage) => void;

interface BridgeOptions {
  allowedHostOrigins: string[];
  cryptoKey?: string;
  debug?: boolean;
  enableNavigationSync?: boolean; // 👈 NEW
}

let isInitialized = false;

let globalConfig: { appId: string; origins: string[] } | null = null;
let globalBridge: ReturnType<typeof setupBridge> | null = null;

export function createBridge(appId: string, options: BridgeOptions) {
  if (globalBridge) {
    if (
      globalConfig?.appId !== appId ||
      JSON.stringify(globalConfig?.origins) !== JSON.stringify(options.allowedHostOrigins)
    ) {
      console.warn("⚠️ Bridge already initialized with different config");
    }

    return globalBridge;
  }

  globalConfig = {
    appId,
    origins: options.allowedHostOrigins
  };

  globalBridge = setupBridge(appId, options);

  return globalBridge;
}

export function resetBridge() {
  globalBridge?.destroy();
  globalBridge = null;
}

export function signalBridge() {
  if (!globalBridge) {
    return;
    // throw new Error("❌ Bridge belum di-init. Panggil createBridge().init() dulu");
  }

  return globalBridge;
}


function setupBridge(
  appId: string,
  options: BridgeOptions
) {
  const { allowedHostOrigins, cryptoKey, debug } = options;

  const isRemote = window.parent && window.parent !== window;

  if (!isRemote) {
    throw new Error("Signal bridge hanya boleh dijalankan di iframe (remote).");
  }

  const listeners: Listener[] = [];

  const log = (...args: any[]) => {
    if (debug) console.log("[🛰️DEBUG-SIGNAL-BRIDGE]", ...args);
  };

  log("Initialized (REMOTE)");

  // =====================================================
  // RECEIVE MESSAGE FROM HOST
  // =====================================================
  const messageHandler = (event: MessageEvent) => {
    const data = event.data;
    const origin = event.origin;

    if (!data || typeof data !== "object" || !data.type) return;
    if (!allowedHostOrigins.includes(origin)) return;
    if (data.__source === "remote") return; // anti loop

    let payload = data.payload;

    if (cryptoKey && typeof payload === "string" && payload) {
      payload = decrypt(payload, cryptoKey);
    }

    const remoteMessage: BridgeMessage = {
      type: data.type,
      payload,
      txn: data.txn || "",
      __source: "host",
      __origin: origin,
    };

    listeners.forEach((fn) => fn(remoteMessage));
  };

  window.addEventListener("message", messageHandler);

  // =====================================================
  // SEND MESSAGE TO HOST
  // =====================================================
  function emitToHost(type: string, payload: any, txn?: string): string {
    let finalPayload = payload;

    const resolvedTxn = txn || crypto.randomUUID();
    if (cryptoKey) {
      finalPayload = encrypt(payload, cryptoKey);
    }

    // if (!txn) {
    //   finalPayload = encrypt(payload, resolvedTxn)
    // }


    const message = {
      app_id: appId,
      type,
      payload: finalPayload,
      txn: resolvedTxn,
      __origin: window.location.origin,
      __source: "remote",
    };

    if (window.parent && window.parent !== window) {
      allowedHostOrigins.forEach((origin) => {
        window.parent!.postMessage(message, origin);
      });
    }

    return resolvedTxn;
  }

  function emitToHostEncrypted(type: string, payload: any): { txn: string; key: string } {
    const key = generateKey();
    const finalPayload = encrypt(payload, key);

    const message = {
      app_id: appId,
      type,
      payload: finalPayload,
      txn: key,
      __origin: window.location.origin,
      __source: "remote",
    };

    if (window.parent && window.parent !== window) {
      allowedHostOrigins.forEach((origin) => {
        window.parent!.postMessage(message, origin);
      });
    }

    return { txn: key, key };
  }

  // =====================================================
  // NAVIGATION SYNC (SPA SAFE)
  // =====================================================


  // initial sync
  function emitReadyOnce() {
    if (isInitialized) return;

    isInitialized = true;
    log("READY emitted");

    emitToHost("ready", {
      ok: true,
      app_id: appId,
      timestamp: Date.now()
    });
  }


  // =====================================================
  // PUBLIC API
  // =====================================================
  return {

    init() {
      emitReadyOnce();
    },

    listen(handler: Listener) {
      listeners.push(handler);
    },

    /**
     * Kirim pesan ke host tanpa enkripsi otomatis.
     *
     * Gunakan ini jika host dan remote sudah punya `cryptoKey` yang sama (static key),
     * atau jika payload memang tidak perlu dienkripsi.
     * Untuk enkripsi otomatis per-pesan, gunakan `emitEncrypted`.
     *
     * @param event   Nama event, contoh: `"ping"`, `"user:logout"`
     * @param payload Data yang ingin dikirim
     * @param txn     (opsional) Transaction ID custom. Auto-generate UUID jika tidak diisi.
     * @returns `txn` yang digunakan — berguna untuk tracking response dari host.
     *
     * @example
     * const txn = signalBridge.emit("ping", { ts: Date.now() });
     */
    emit(event: string, payload: any, txn?: string): string | undefined {
      if (!isRemote) return;
      return emitToHost(event, payload, txn);
    },

    /**
     * Kirim pesan ke host dengan enkripsi otomatis.
     *
     * Setiap pemanggilan menghasilkan key acak baru (CSPRNG),
     * payload di-encrypt dengan key tersebut
     *
     * @param event    Nama event, contoh: `"user:login"`, `"cart:update"`
     * @param payload Data apapun (object, string, dll) yang ingin dikirim
     * @returns `{ txn, key }` — keduanya bernilai sama (ephemeral key).
     *   Simpan jika perlu referensi untuk matching response dari host.
     *
     * @example
     * const { txn } = bridge.emitEncrypted("order:submit", { id: 123 });
     *
     */
    emitEncrypted(event: string, payload: any): { txn: string; key: string } | undefined {
      if (!isRemote) return;
      return emitToHostEncrypted(event, payload);
    },


    /**
     * Kumpulan tracker event aktivitas pengguna.
     * Setiap method mengirim event ke host dengan enkripsi otomatis (ephemeral key via `txn`).
     *
     * Event yang tersedia:
     * - `open_file`     — pengguna membuka file
     * - `open_document` — pengguna membuka dokumen
     * - `view_document` — pengguna melihat/membaca dokumen
     *
     * Event name dikirim dengan prefix `activity:`, contoh: `"activity:open_file"`.
     *
     * @returns `{ txn, key }` — ephemeral key yang dipakai untuk enkripsi pesan ini.
     *
     * @example
     * bridge.track.open_file({ name: "laporan.pdf", size: 204800 })
     * bridge.track.open_document({ docId: "DOC-001", title: "Invoice" })
     * bridge.track.view_document({ docId: "DOC-001", duration: 30 })
     */
    track: (() => {
      const EVENTS = [
        "open_file",
        "open_document",
        "view_document",
      ] as const;

      type TrackEvent = typeof EVENTS[number];
      type Tracker = { [K in TrackEvent]: (payload?: any) => { txn: string; key: string } | undefined };

      return EVENTS.reduce((acc, event) => {
        acc[event] = (payload?: any) => {
          if (!isRemote) return;
          return emitToHostEncrypted(`activity:${event}`, payload ?? {});
        };
        return acc;
      }, {} as Tracker);
    })(),

    destroy() {
      window.removeEventListener("message", messageHandler);
    },
  };
}

export function initBridge(appId: string, options: BridgeOptions) {
  const instance = createBridge(appId, options);
  instance.init();
  return instance;
}

export * from "./crypto";
export * from "./activity-reporter";

// 👇 UMD / browser global support
if (typeof window !== "undefined") {
  (window as any).signalBridge = Object.assign(signalBridge, {
    init: initBridge, // 👈 pakai ini (auto init)
    createBridge,
    reset: resetBridge,
    encrypt,
    decrypt,
    generateKey,
    activity,
  });
}